// ─────────────────────────────────────────────────────────────────────────
// educationRules — region profiles, recency ordering and level hierarchy for
// repeatable groups.
//
// THE REQUIREMENTS THIS SERVES
//
//  1. "In the admin create a region US / UK / IND / common at the top. From
//     this it has to trigger the components … when I click US and UK and apply
//     the changes it has to render all."
//
//  2. "For US and UK it can proceed with currently pursuing" (a candidate may
//     apply mid-degree) "but in case of India only after pursuing will a
//     company let you apply."
//
//  3. "If I click currently pursuing in the middle of the rows it has to show
//     a popup — 'as you are mentioning currently pursuing, since this seems to
//     be a recent education can I make this the 1st?' If the user clicks yes
//     then it has to be at the top, and it has to be in the order using the
//     start date and end date, recent first."
//
//  4. "In the education, if I type Masters in the latest and go to the next
//     group it has to show the error 'you added only the PG, the UG degree is
//     mandatory'."
//
// NOTHING HERE NAMES A REGION, A DEGREE OR A MODULE. A region is a key into a
// config map; a degree's rank comes from master data. That is what lets the
// same code serve a market nobody has thought of yet.
// ─────────────────────────────────────────────────────────────────────────
import dayjs from 'dayjs';

const text = (v) => (v === null || v === undefined ? '' : String(v).trim());

// ── 1. region profiles ───────────────────────────────────────────────────

/**
 * resolveRegionRules — merge a group's `regionRules[<active region>]` over its
 * base config.
 *
 * A group declares its defaults normally and overrides only what differs per
 * region, so switching region re-renders every affected component with no
 * branch in any component and no second copy of the config.
 *
 * An unknown/absent region falls back to `default`, then to the base group —
 * so a tenant that never sets a region behaves exactly as before.
 */
export function resolveRegionRules(group, region) {
  const rules = group?.regionRules;
  if (!rules || typeof rules !== 'object') return group ?? {};
  const key = text(region);
  const applied = rules[key] ?? rules[key.toUpperCase()] ?? rules.default;
  if (!applied || typeof applied !== 'object') return group ?? {};
  return { ...group, ...applied };
}

// ── 2. "currently pursuing" allowed by region ────────────────────────────

/**
 * inProgressAllowed — may a row be marked as still in progress?
 *
 * Defaults to TRUE: blocking is the exceptional rule (India), and a group that
 * never configures this must not suddenly start rejecting rows.
 */
export function inProgressAllowed(group) {
  return group?.allowInProgress !== false;
}

/**
 * inProgressError — the configured message for a disallowed in-progress row,
 * or '' when it is allowed.
 */
export function inProgressError(group) {
  if (inProgressAllowed(group)) return '';
  if (inProgressWarns(group)) {
    return group?.inProgressWarningMessage
      || group?.inProgressMessage
      || 'In this region an unfinished qualification is not usually accepted when a '
         + 'candidate is put forward for a role. You can still record it here \u2014 just '
         + 'be aware it will have to be completed before they can be submitted.';
  }
  return group?.inProgressMessage
    || 'This one needs to be finished before the application can go ahead. '
       + 'Please untick \u201cstill ongoing\u201d, or remove the row.';
}

/**
 * inProgressSeverity \u2014 HOW HARD a region's restriction bites.
 *
 * The same fact does not carry the same weight everywhere it appears. Recording
 * a candidate is record-keeping: someone mid-degree in India is a real person
 * whose details are worth having on file, and refusing to record them throws
 * away information the business wanted. Putting that candidate FORWARD is a
 * commitment to a client who will not accept an unfinished qualification \u2014 and
 * there the same fact has to stop the submit.
 *
 *   'allow'  no restriction (the default everywhere)
 *   'warn'   say so, and let it through
 *   'block'  refuse \u2014 the tick is reversed and the form will not submit
 *
 * Defaults to 'block' when allowInProgress is false, so a group configured
 * before this key existed behaves exactly as it did.
 */
export function inProgressSeverity(group) {
  if (inProgressAllowed(group)) return 'allow';
  const configured = text(group?.inProgressSeverity).toLowerCase();
  return configured === 'warn' ? 'warn' : 'block';
}

/** Does this group refuse an in-progress row outright? */
export function inProgressBlocks(group) {
  return inProgressSeverity(group) === 'block';
}

/** Does this group merely caution about one? */
export function inProgressWarns(group) {
  return inProgressSeverity(group) === 'warn';
}

/**
 * buildInProgressValidator \u2014 the SUBMIT-time half of a blocking rule.
 *
 * Intercepting the tick-box is not enough on its own. A submission's rows are
 * prefilled from the candidate, and the candidate is allowed to carry an
 * in-progress entry \u2014 so a blocked row arrives without anybody having clicked
 * anything. Without this the form would accept it, and the rule would hold only
 * against users who happened to tick the box by hand.
 *
 * Returns null when the group does not block, so no rule is attached at all.
 */
export function buildInProgressValidator(group) {
  if (!inProgressBlocks(group)) return null;
  const message = inProgressError(group);
  return {
    validator: (_, value) => (value === true
      ? Promise.reject(new Error(message))
      : Promise.resolve()),
  };
}

// ── 3. recency ordering ──────────────────────────────────────────────────

/**
 * rowSortKey — the instant a row is ordered by. End date first (a finished
 * qualification is placed by when it finished), falling back to start date.
 * Returns null when the row carries no usable date, so undated rows can be
 * kept where they are rather than being shuffled to an arbitrary end.
 */
export function rowSortKey(row, cfg = {}) {
  const endField = cfg.tieBreak ?? 'endDate';
  const startField = cfg.field ?? 'startDate';
  for (const key of [endField, startField]) {
    const value = row?.[key];
    if (value === undefined || value === null || value === '') continue;
    const d = dayjs(value);
    if (d.isValid()) return d.valueOf();
  }
  return null;
}

/**
 * isInProgressRow — is this row flagged as ongoing?
 */
export function isInProgressRow(row, cfg = {}) {
  const field = cfg.inProgressField ?? 'currentStudyingHere';
  return Boolean(row?.[field]);
}

/**
 * orderedRowIndexes — the indexes the rows SHOULD appear in.
 *
 * Most recent first. An in-progress row sorts above every completed one when
 * `inProgressFirst` is set — it is by definition the latest, and it usually has
 * no end date to sort on. Undated rows keep their relative position at the end
 * rather than being flung to the top by a null comparing as zero.
 */
export function orderedRowIndexes(rows = [], cfg = {}) {
  const desc = (cfg.direction ?? 'desc') === 'desc';
  const decorated = rows.map((row, index) => ({
    index,
    key: rowSortKey(row, cfg),
    ongoing: cfg.inProgressFirst !== false && isInProgressRow(row, cfg),
  }));

  return decorated
    .slice()
    .sort((a, b) => {
      if (a.ongoing !== b.ongoing) return a.ongoing ? -1 : 1;
      // Undated rows sink, and hold their original order among themselves.
      if (a.key === null && b.key === null) return a.index - b.index;
      if (a.key === null) return 1;
      if (b.key === null) return -1;
      if (a.key === b.key) return a.index - b.index;
      return desc ? b.key - a.key : a.key - b.key;
    })
    .map((d) => d.index);
}

/**
 * misplacedRow — where does the row the user just edited actually belong?
 *
 * Returns null when it is already in the right place, else
 * { from, to, reason } where reason is 'inProgress' or 'outOfOrder' — which is
 * what lets the caller pick between the requirement's two different prompts.
 */
export function misplacedRow(rows, changedIndex, cfg = {}) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  if (changedIndex == null || changedIndex < 0 || changedIndex >= rows.length) return null;

  const order = orderedRowIndexes(rows, cfg);
  const to = order.indexOf(changedIndex);
  if (to === -1 || to === changedIndex) return null;

  return {
    from: changedIndex,
    to,
    reason: isInProgressRow(rows[changedIndex], cfg) ? 'inProgress' : 'outOfOrder',
  };
}

// Default prompt wording. Both sentences are the ones the requirement asks for,
// and both are overridable per group via `orderBy.messages`.
export const DEFAULT_ORDER_MESSAGES = Object.freeze({
  inProgress: 'You\u2019ve marked this one as still ongoing, so it\u2019s the most recent. '
    + 'Shall we move it to the top? This list reads best newest first.',
  outOfOrder: 'These dates make this the most recent one. '
    + 'Shall we move it to the top? This list reads best newest first.',
  ok: 'Yes, move it up',
  keep: 'No, leave it here',
});

/**
 * orderPromptMessage — the sentence for a given misplacement.
 */
export function orderPromptMessage(reason, cfg = {}) {
  const messages = { ...DEFAULT_ORDER_MESSAGES, ...(cfg.messages ?? {}) };
  return messages[reason] ?? messages.outOfOrder;
}

/**
 * moveRow — pure reorder, so the caller can preview or test it without antd.
 */
export function moveRow(rows, from, to) {
  const next = [...(rows ?? [])];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length) return next;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

// ── 4. degree hierarchy ──────────────────────────────────────────────────

/**
 * levelOf — a row's rank, resolved from master data rather than from the
 * degree's NAME. `levels` maps a stored value to a numeric rank
 * ({ "UG": 4, "PG": 5, … }), which is what keeps "Masters", "M.Tech" and
 * "PG" configurable as the same rank without any of them appearing here.
 */
export function levelOf(row, cfg = {}) {
  const field = cfg.levelField ?? 'qualification';
  const raw = text(row?.[field]);
  if (!raw) return null;
  const levels = cfg.levels ?? {};
  const direct = levels[raw] ?? levels[raw.toLowerCase()] ?? levels[raw.toUpperCase()];
  if (Number.isFinite(Number(direct))) return Number(direct);
  return patternLevel(raw, cfg);
}

/**
 * patternLevel — match a FREE-TEXT qualification to a level by keyword.
 *
 * The exact `levels` map above works when the stored value is a short code
 * ("UG", "PG"). It is useless against the real data: the qualification master
 * holds ~111 spelled-out names — "MASTER OF SCIENCE", "BACHELOR OF ENGINEERING",
 * "M.SC.", "MBBS", "Diploma" — so a record saying "Master of Science in Computer
 * Science" matched no key, resolved to no level, and the whole hierarchy rule
 * quietly evaluated to nothing.
 *
 * Enumerating all 111 in the map is not a fix: it breaks the day somebody adds
 * a degree, and it breaks silently, in the same way.
 *
 * So `levelPatterns` is admin config — an ORDERED list of { match, level },
 * first match wins, tested case-insensitively. Order is the admin's lever: put
 * the more specific rungs first, because "Bachelor of Engineering and Master of
 * Science" legitimately matches two.
 *
 * A malformed pattern is skipped, not thrown: one bad regex in config must not
 * take the submit button down with it.
 */
function patternLevel(raw, cfg) {
  const patterns = Array.isArray(cfg?.levelPatterns) ? cfg.levelPatterns : [];
  const subject = raw.toLowerCase();
  for (const entry of patterns) {
    const pattern = text(entry?.match);
    if (!pattern) continue;
    let matcher;
    try {
      matcher = new RegExp(pattern, 'i');
    } catch {
      // A malformed pattern is SKIPPED, never thrown: one bad regex in config
      // must not take the submit button down with it.
      continue;
    }
    if (matcher.test(subject) && Number.isFinite(Number(entry.level))) {
      return Number(entry.level);
    }
  }
  return null;
}

/**
 * levelLabel — how a level is NAMED to the user.
 *
 * The rule's keys are codes ("UG"), and a message reading "there is no UG
 * recorded" tells a recruiter nothing. `levelLabels` maps each code to the words
 * a person uses. Falls back to the code, so a level nobody has labelled still
 * names itself rather than disappearing.
 */
export function levelLabel(code, cfg = {}) {
  const labels = cfg?.levelLabels ?? {};
  return text(labels[code]) || text(code);
}

/**
 * missingRequiredLevels — which mandatory levels BELOW the highest entered one
 * are absent.
 *
 * "You added only the PG; the UG degree is mandatory" is exactly this: the
 * highest level present is PG (5), UG (4) is configured as required, and no row
 * carries it.
 *
 * Returns [] when the rule is not configured, when nothing has been entered
 * yet, or when `requireBelow` is off (US/UK, where applying mid-degree is
 * normal) — so it never fires on a form the rule was not meant for.
 */
export function missingRequiredLevels(rows = [], cfg = {}) {
  if (!cfg || cfg.requireBelow === false) return [];
  const levels = cfg.levels ?? {};
  const required = cfg.requiredLevels ?? [];
  if (!required.length) return [];

  const present = rows.map((r) => levelOf(r, cfg)).filter((n) => n !== null);
  if (!present.length) return [];
  const highest = Math.max(...present);

  const missing = [];
  required.forEach((entry) => {
    // An entry may be a label ("UG") or {label, level}.
    const label = typeof entry === 'object' ? entry.label : entry;
    const rank = typeof entry === 'object' && Number.isFinite(Number(entry.level))
      ? Number(entry.level)
      : Number(levels[label]);
    if (!Number.isFinite(rank)) return;
    // Only levels BELOW what the candidate claims are required: someone whose
    // highest entry is 12th grade must not be asked for a degree.
    if (rank >= highest) return;
    if (!present.includes(rank)) missing.push(label);
  });
  return missing;
}

/**
 * levelRuleError — the finished message, or '' when the rule is satisfied.
 */
export function levelRuleError(rows, cfg = {}) {
  const missing = missingRequiredLevels(rows, cfg);
  if (!missing.length) return '';
  // Named the way a person would say it, not by the config's code.
  const list = missing.map((code) => levelLabel(code, cfg)).join(', ');
  const template = cfg.message
    || 'You\u2019ve added a higher qualification but not the {missing} below it. '
       + 'Please add that too.';
  return template.replace('{missing}', list);
}

/**
 * promptRowMove — ask whether to move a row that is now out of order, and do it.
 *
 * Called after the user changes something that affects a row's position: the
 * dates, or the "still ongoing" tick-box. If the row belongs somewhere else,
 * they are asked; nothing is ever reordered behind their back, because a list
 * that rearranges itself while you are typing in it is disorienting.
 *
 * Returns true when a move happened, so the caller can skip any follow-up work.
 *
 * @param {object}   opts
 * @param {object}   opts.group      the group config (already region-resolved)
 * @param {Array}    opts.rows       the group's current rows
 * @param {number}   opts.rowIndex   the row the user just edited
 * @param {function} opts.move       Form.List's move(from, to)
 * @param {function} opts.confirm    ({title, body, okText, cancelText}) => Promise<boolean>
 */
export async function promptRowMove({ group, rows, rowIndex, move, confirm }) {
  const cfg = group?.orderBy;
  if (!cfg || cfg.confirmMove === false || typeof move !== 'function') return false;

  const misplaced = misplacedRow(rows, rowIndex, cfg);
  if (!misplaced) return false;

  const messages = { ...DEFAULT_ORDER_MESSAGES, ...(cfg.messages ?? {}) };
  const agreed = await confirm({
    reason: misplaced.reason,
    title: misplaced.reason === 'inProgress'
      ? 'This looks like the most recent one'
      : 'These dates make this the most recent one',
    body: orderPromptMessage(misplaced.reason, cfg),
    okText: messages.ok,
    cancelText: messages.keep,
    from: misplaced.from,
    to: misplaced.to,
  });
  if (!agreed) return false;

  move(misplaced.from, misplaced.to);
  return true;
}

// ── 5. the cross-module gate ─────────────────────────────────────────────

/**
 * groupInProgressConfig — a group's ongoing-flag settings, region-resolved.
 *
 * Returns null for a group that has no ongoing flag at all, so a caller can
 * simply skip it. `rowsKey` is where the group's rows live on a stored record
 * (payloadKey, falling back to the group name) — the popup needs that to read a
 * candidate it did not render.
 */
export function groupInProgressConfig(group, region) {
  const effective = resolveRegionRules(group, region);
  const inProgressField = effective?.orderBy?.inProgressField;
  if (!inProgressField) return null;
  return {
    name: effective.name,
    label: effective.label ?? effective.name,
    rowsKey: effective.payloadKey || effective.name,
    inProgressField,
    severity: inProgressSeverity(effective),
    message: inProgressError(effective),
  };
}

/**
 * inProgressVerdict — what a MODULE's rules say about a RECORD.
 *
 * This is the single question both sides of the flow ask, and the reason it
 * lives here rather than in either of them: Quick Submit has to refuse exactly
 * what the submission form would refuse. Two implementations of "does this
 * candidate have an unfinished qualification" would drift, and the failure mode
 * is the worst kind — the popup lets someone through to a form that then will
 * not submit, with no way back.
 *
 *   groups   the TARGET module's form groups (submissions, when gating a
 *            submit) — never the source record's own module
 *   region   the tenant's configured region
 *   record   the record being judged (a candidate), whose rows are read by
 *            each group's rowsKey
 *
 * Returns { severity, message, group } — severity 'allow' when nothing
 * objects, so a caller can treat any other value as "say something".
 */
export function inProgressVerdict(groups = [], region, record) {
  if (!record) return { severity: 'allow', message: '', group: null };

  let warning = null;
  for (const group of groups) {
    const cfg = groupInProgressConfig(group, region);
    if (!cfg || cfg.severity === 'allow') continue;

    const rows = readRows(record, cfg.rowsKey);
    if (!rows.some((row) => isInProgressRow(row, cfg))) continue;

    // A block is final; keep looking only while all we have is a warning, so
    // one group that merely cautions never masks another that refuses.
    if (cfg.severity === 'block') {
      return { severity: 'block', message: cfg.message, group: cfg };
    }
    warning = warning ?? { severity: 'warn', message: cfg.message, group: cfg };
  }
  return warning ?? { severity: 'allow', message: '', group: null };
}

/**
 * readRows pulls a group's rows off a stored record.
 *
 * Tolerates the three shapes one arrives in: the plain array a record carries,
 * the `{ rows }` wrapper getFormGroups embeds for edit-prefill, and a missing
 * key. Anything else yields no rows — which means "nothing to object to", the
 * safe answer for a shape we do not understand.
 */
function readRows(record, key) {
  const raw = record?.[key];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.rows)) return raw.rows;
  return [];
}

// ── 6. the level rule at SUBMIT time ─────────────────────────────────────

/**
 * levelRuleSeverity — how hard a missing lower qualification bites.
 *
 *   'warn'  (default) ask, and let the user go ahead
 *   'block' refuse the submit
 *
 * Defaults to WARN, and deliberately so. This rule has never actually fired —
 * the config and the functions existed with nothing calling them — so switching
 * it on as a hard block would start rejecting submissions that have always been
 * accepted, for a reason nobody has seen before. A question the user can answer
 * introduces the same rule without that.
 *
 * A record is also not always wrong: someone genuinely may hold a Master's from
 * a system that never recorded the Bachelor's, and a recruiter looking at the
 * CV knows that better than a config does.
 */
export function levelRuleSeverity(cfg) {
  return String(cfg?.severity ?? '').toLowerCase() === 'block' ? 'block' : 'warn';
}

/**
 * levelRuleVerdict — check EVERY group that has a level rule, for one form's
 * values.
 *
 *   groups  the module's form groups (region-resolved by the caller)
 *   values  the submitted form values; each group's rows are read from its
 *           own name, falling back to its payloadKey
 *
 * Returns { severity, message, group } with severity 'allow' when nothing
 * objects. A BLOCK anywhere wins over a warning, so a group that merely
 * cautions can never mask one that refuses.
 */
export function levelRuleVerdict(groups = [], values = {}) {
  let warning = null;
  for (const group of groups) {
    const cfg = group?.levelRule;
    if (!cfg) continue;
    const rows = rowsForGroup(values, group);
    if (!rows.length) continue;
    const message = levelRuleError(rows, cfg);
    if (!message) continue;
    if (levelRuleSeverity(cfg) === 'block') {
      return { severity: 'block', message, group };
    }
    warning = warning ?? { severity: 'warn', message, group };
  }
  return warning ?? { severity: 'allow', message: '', group: null };
}

/** A group's submitted rows, under its name or its payloadKey. */
function rowsForGroup(values, group) {
  for (const key of [group?.name, group?.payloadKey]) {
    if (!key) continue;
    const raw = values?.[key];
    if (Array.isArray(raw)) return raw;
  }
  return [];
}
